import { useState, useCallback } from "react";
import { sendFirebaseOTP, resetRecaptcha } from "@/lib/firebase";
import { toast } from "sonner";

/**
 * useFirebaseOTP - manages phone OTP send + verify with Firebase.
 * All errors are caught, toasted, and returned as boolean success.
 *
 * Usage:
 *   const otp = useFirebaseOTP();
 *   const ok = await otp.sendOTP('9876543210'); // will send +91 prefix
 *   if (ok) { ... show OTP input ...
 *     const idToken = await otp.verifyOTP('123456');
 *     if (idToken) callBackend(idToken);
 *   }
 */
const OTP_TIMEOUT_MS = 30000;

const friendlyOtpError = (err) => {
  switch (err?.code) {
    case "auth/invalid-phone-number": return "Invalid phone number";
    case "auth/too-many-requests": return "Too many attempts. Please try again later.";
    case "otp/timeout": return "OTP is taking too long. Please try again.";
    default: return "We couldn't send the OTP right now. Please try again.";
  }
};

export const useFirebaseOTP = () => {
  const [confirmation, setConfirmation] = useState(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [mobile, setMobile] = useState("");

  const sendOTP = useCallback(async (rawMobile, countryCode = "+91") => {
    setSending(true);
    let timer;
    try {
      const digits = String(rawMobile || "").replace(/\D/g, "");
      const last10 = digits.slice(-10);
      if (last10.length !== 10) {
        toast.error("Please enter a valid 10-digit mobile number");
        return false;
      }
      const phoneE164 = `${countryCode}${last10}`;
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(Object.assign(new Error("timeout"), { code: "otp/timeout" })), OTP_TIMEOUT_MS);
      });
      const conf = await Promise.race([sendFirebaseOTP(phoneE164), timeout]);
      setConfirmation(conf);
      setMobile(last10);
      toast.success(`OTP sent to ${phoneE164}`);
      return true;
    } catch (err) {
      console.error("Firebase sendOTP error", err);
      toast.error(friendlyOtpError(err));
      resetRecaptcha();
      return false;
    } finally {
      clearTimeout(timer);
      setSending(false);
    }
  }, []);

  const verifyOTP = useCallback(async (otp) => {
    if (!confirmation) {
      toast.error("Please request OTP first");
      return null;
    }
    setVerifying(true);
    try {
      const result = await confirmation.confirm(String(otp).trim());
      const idToken = await result.user.getIdToken();
      return idToken;
    } catch (err) {
      console.error("Firebase verifyOTP error", err);
      toast.error(
        err?.code === "auth/invalid-verification-code"
          ? "Wrong OTP. Please check and try again."
          : err?.code === "auth/code-expired"
          ? "OTP expired. Please request a new one."
          : err?.message || "OTP verification failed"
      );
      return null;
    } finally {
      setVerifying(false);
    }
  }, [confirmation]);

  const reset = useCallback(() => {
    setConfirmation(null);
    setMobile("");
    resetRecaptcha();
  }, []);

  return { sendOTP, verifyOTP, reset, sending, verifying, otpSent: !!confirmation, mobile };
};
